import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export const FAQPage = () => {
  const faqs = [
    {
      question: "Do you travel?",
      answer: "Yes — travel within 45 miles of Atlanta is included. Beyond that, we charge $1.25 per mile. We're happy to bring the Royal Delights experience to your location."
    },
    {
      question: "Do you bring tables and chairs?",
      answer: "Yes — tables and chairs are available as an add-on. Just let us know your needs during the booking process, and we'll include them in your quote."
    },
    {
      question: "Can you cook indoors?",
      answer: "Yes, depending on space and ventilation. We assess each location to ensure safety and quality. Indoor events work great in well-ventilated spaces, patios, or large kitchens."
    },
    {
      question: "Do you offer vegan options?",
      answer: "Absolutely — see our Vegan Royalty section on the menu. We offer Teriyaki Plant-Based Chicken, Vegetable Spring Rolls, and Cauliflower Fried Rice, all prepared with the same care and luxury."
    },
    {
      question: "What is the Royal Experience Fee?",
      answer: "A premium service fee covering setup, equipment, fuel, coordination, and breakdown. It's $150 for events under 30 guests and $250 for 30+ guests. Full details appear on your invoice."
    },
    {
      question: "What is the minimum order?",
      answer: "We have an $850 minimum invoice for all events. This ensures we can deliver the full Royal Delights experience with proper staffing and preparation."
    },
    {
      question: "How far in advance should I book?",
      answer: "We recommend booking at least 2-3 weeks in advance to secure your preferred date. For peak seasons and holidays, earlier booking is advised. Last-minute requests may be accommodated based on availability."
    },
    {
      question: "What is included in the booking fee?",
      answer: "The $200 booking fee secures your date and goes toward your final invoice. It covers initial consultation, menu planning, and reservation of your event date. It's non-refundable but is applied to your total."
    },
    {
      question: "Do kids eat free?",
      answer: "Children under 7 eat free! For kids ages 8-12, we offer our Kids Royal Menu at $20, which includes their choice of chicken, steak, or shrimp with fried rice and vegetables."
    },
    {
      question: "How does staffing work?",
      answer: "Our Royal Staff handles everything: setup, hospitality, service, clearing, refilling, cleanup, and breakdown. Plated dinners include a dedicated server. For buffets, we provide 1 staff for under 15 guests, 2 for 15-30 guests, and additional staff for larger events at $125 each."
    },
    {
      question: "Can you accommodate allergies and dietary restrictions?",
      answer: "Yes, please communicate all allergies and dietary restrictions when booking. We take food safety seriously and will work with you to ensure all guests can enjoy the experience safely."
    },
    {
      question: "What's the difference between plated and buffet service?",
      answer: "Plated service (2-12 guests, $65-$95/person) offers an intimate, chef-driven experience with refined plating and a dedicated server. Buffet service (10-40 guests, $55-$100/person) provides a celebratory spread perfect for larger gatherings, with staffing billed separately."
    }
  ];

  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4 font-body">
              Support
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F0] mb-6">
              Frequently Asked Questions
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA]">
              Everything you need to know about the Royal Delights experience.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 lg:py-24 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div key={index} variants={fadeInUp}>
                  <AccordionItem 
                    value={`item-${index}`} 
                    className="bg-[#121212] border border-[#27272A] px-6"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="font-heading text-lg text-[#F5F5F0] hover:text-[#D4AF37] py-6 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-[#A1A1AA] pb-6 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-24 lg:py-32 bg-[#121212]">
        <motion.div 
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-[#F5F5F0] mb-6"
          >
            Still Have Questions?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-[#A1A1AA] mb-10"
          >
            We're here to help. Reach out and we'll get back to you as soon as possible.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="faq-contact-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:scale-105"
              >
                Contact Us
              </Button>
            </Link>
            <Link to="/booking">
              <Button 
                variant="outline"
                data-testid="faq-book-btn"
                className="border border-white/20 text-[#F5F5F0] hover:bg-white/10 bg-transparent rounded-full px-8 py-4 transition-all duration-300"
              >
                Book Now
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default FAQPage;
